import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { MAIN_MAP } from '../../../../../redux/map/Constants';
import { NEW_SKETCHES } from '../../../../../redux/panel/PanelNames';
import { showSecondaryPanel } from '../../../../../redux/panel/Actions';
import {
    setShapeFeatureProperty,
    savedDrawnFeaturesAsync,
    fetchAllDrawingsAsync
} from '../../../../../redux/map/Actions';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    makeStyles
} from '@material-ui/core';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';

import TextField from '../../../../Common/UI/TextField';
import DialogButton from '../../../../Common/UI/Dialog/DialogButton';

const propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    feature: PropTypes.object
};

const defaultProps = {};

const useStyles = makeStyles(theme => styles(theme));

function SaveSketchDialog({ open, onClose, feature }) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [name, setName] = useState('');
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const drawModule = useSelector(state => state[MAIN_MAP].drawModule);
    const drawingFeature = useSelector(state => state[MAIN_MAP].drawingFeature.id);
    const savedDrawnFeatures = useSelector(state => state[MAIN_MAP].savedDrawnFeatures);
    const projectId = useSelector(state => state.project.currentProject.id);
    const { drawControl } = drawModule;

    function dispatchSetShapeFeatureProperty(userProperty, userPropertyValue) {
        return dispatch(
            setShapeFeatureProperty(drawingFeature, userProperty, userPropertyValue, MAIN_MAP)
        );
    }
    function dispatchShowSecondaryPanel(name) {
        return dispatch(showSecondaryPanel(name));
    }
    function dispatchSetSavedDrawnFeaturesAsync(params) {
        return dispatch(savedDrawnFeaturesAsync(projectId, params));
    }
    function dispatchFetchDrawnFeaturesAsync() {
        return dispatch(fetchAllDrawingsAsync(projectId));
    }

    function setDefaultPropertyOnShape(feature) {
        if (!name) {
            // Check if empty
            setError(true);
            setErrorMessage('Name cannot be empty');
        } else if (savedDrawnFeatures.some(value => value.properties.name === name)) {
            // Check if name already exists
            setError(true);
            setErrorMessage('Name exists already');
        } else {
            setError(false);
            dispatchSetShapeFeatureProperty('fillColour', '#A9FE95');
            dispatchSetShapeFeatureProperty('strokeStyle', 'dashed');
            dispatchSetShapeFeatureProperty('strokeColour', '#A9FE95');
            dispatchSetShapeFeatureProperty('name', name);
            dispatchSetShapeFeatureProperty('saved', true);

            let result = drawControl.get(feature?.id);
            drawControl.add(result);

            dispatchSetSavedDrawnFeaturesAsync(result);
            dispatchShowSecondaryPanel(NEW_SKETCHES);
            onClose();
        }
    }

    useEffect(() => {
        dispatchFetchDrawnFeaturesAsync();
    }, [projectId]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle disableTypography>
                <SaveOutlinedIcon />
                <Typography variant="h4">Save Shape</Typography>
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    name="name"
                    error={Boolean(error)}
                    helperText={Boolean(error) ? errorMessage : null}
                    autoFocus
                    fullWidth
                    label="Shape Name"
                    value={name}
                    placeholder="Ensure the name is unique and not already in use"
                    onChange={e => setName(e.target.value)}
                />
            </DialogContent>
            <DialogActions classes={{ root: classes.rootPadding }}>
                <DialogButton
                    variant="contained"
                    disabled={!Boolean(name)}
                    classes={{ root: classes.saveButton }}
                    onClick={() => setDefaultPropertyOnShape(feature)}>
                    Save
                </DialogButton>
                <DialogButton
                    onClick={onClose}
                    variant="outlined"
                    classes={{ root: classes.cancelButton }}>
                    Cancel
                </DialogButton>
            </DialogActions>
        </Dialog>
    );
}

const styles = () => ({
    saveButton: {
        height: 40
    },
    cancelButton: {
        marginLeft: 24,
        height: 40
    },
    rootPadding: {
        padding: '20px 30px 24px 30px'
    }
});

SaveSketchDialog.prototype = propTypes;
SaveSketchDialog.defaultProps = defaultProps;

export default SaveSketchDialog;
